import { ColumnType, OrderColumnDeepFragment } from '../../../gql/graphql';

export const getTranslatedDefaultColumnName = (
  columnName: string,
  translate: (key: string) => string
) => {
  return columnMapping[columnName]
    ? translate('table:default-column-name.' + columnMapping[columnName])
    : columnName;
};

export const columnMapping: { [key: string]: string } = {
  Title: 'title',
  Quantity: 'quantity',
  Table: 'parentOrder',
  Unit: 'unit',
  Color: 'color',
  'Unit cost': 'unitPrice',
  'Table Status': 'parentOrderStatus',
  Elements: 'linkedCount',
};

export const columnNameToColumnId = (
  columnName: string,
  orderColumns: OrderColumnDeepFragment[],
  columnType?: ColumnType | null
): string => {
  // First check if there's a column with this name and the specified type
  const nameInCustomCol = orderColumns.find(
    (col) => col.name === columnName && col.columnType === columnType
  );

  // For custom column types, use the column ID directly
  if (
    nameInCustomCol &&
    columnType &&
    [
      ColumnType.Custom,
      ColumnType.ParentOrderLabel,
      ColumnType.ItemGroup,
    ].includes(columnType)
  ) {
    return nameInCustomCol.id;
  }

  // For standard column types, map the column name to its standard ID
  // This handles the case where the column might have been renamed
  if (columnMapping[columnName]) {
    return columnMapping[columnName];
  }

  // As a fallback, try to find the column by name only (for backward compatibility)
  const columnByNameOnly = orderColumns.find((col) => col.name === columnName);
  if (columnByNameOnly) {
    return columnByNameOnly.id;
  }

  // If nothing else worked, just return the original name
  return columnName;
};

export const columnIdToColumnName = (columnId: string): string => {
  const entry = Object.entries(columnMapping).find(([, id]) => id === columnId);
  return entry ? entry[0] : columnId;
};

export function columnToColumnId({ id, columnType }: OrderColumnDeepFragment) {
  if (
    columnType === ColumnType.Custom ||
    columnType === ColumnType.ParentOrderLabel ||
    columnType === ColumnType.ItemGroup
  ) {
    return id;
  } else {
    return columnTypeToId(columnType);
  }
}

export function columnIdToColumn(
  columnId: string,
  orderColumns: Array<OrderColumnDeepFragment>
) {
  // We first check if there is a column with this ID and return it
  const nonDefautlColumn = orderColumns.find((oc) => oc.id === columnId);
  if (nonDefautlColumn) return nonDefautlColumn;
  const entry = Object.entries(columnMapping).find(([, id]) => id === columnId);
  if (entry) {
    return orderColumns.find(
      (oc) => oc.name === entry[0] && isDefaultColumn(oc.columnType)
    );
  }
}

function isDefaultColumn(columnType: ColumnType): boolean {
  return ![
    ColumnType.Custom,
    ColumnType.ParentOrderLabel,
    ColumnType.ItemGroup,
  ].includes(columnType);
}

export function columnTypeToId(
  columnType: Exclude<
    ColumnType,
    ColumnType.Custom | ColumnType.ParentOrderLabel | ColumnType.ItemGroup
  >
) {
  // Make sure this mapping is complete for all standard column types
  switch (columnType) {
    case ColumnType.Description:
      return 'title';
    case ColumnType.Unit:
      return 'unit';
    case ColumnType.Color:
      return 'color';
    case ColumnType.Quantity:
      return 'quantity';
    case ColumnType.UnitPrice:
      return 'unitPrice';
    case ColumnType.Total:
      return 'total';
    case ColumnType.LinkedCount:
      return 'linkedCount';
    case ColumnType.ParentOrder:
      return 'parentOrder';
    case ColumnType.ParentOrderStatus:
      return 'parentOrderStatus';
    default: {
      // If we get here, we have an unhandled column type
      console.warn(`Unhandled column type in columnTypeToId: ${columnType}`);
      return String(columnType);
    }
  }
}
